<template>
  <div :class="['m-slider vertical', { disabled: disabled }]" ref="slider" :style="`height: ${height}px;`">
    <div class="u-slider-rail" @click.self="onClickPoint"></div>
    <div class="u-slider-track" :class="{trackTransition: transition}" :style="`
      top: ${top}px;
      bottom: auto;
      height: ${bottom - top}px;
    `"></div>

    <!-- 左滑块 -->
    <div v-if="range" class="u-slider-handle" :class="{handleTransition: transition}"
         tabindex="0"
         ref="left"
         @mousedown="onLeftMouseDown"
         :style="`
        top: ${top}px;
        bottom: auto;
        transform: translateY(-50%);
      `">
      <div class="slider-tooltip" v-show="showLeftTooltip">
        {{ Math.round((height - top) / scale + min) }}
      </div>
    </div>

    <!-- 右滑块 -->
    <div class="u-slider-handle" :class="{handleTransition: transition}"
         tabindex="0"
         ref="right"
         @mousedown="onRightMouseDown"
         :style="`
        top: ${bottom}px;
        bottom: auto;
        transform: translateY(-50%);
      `">
      <div class="slider-tooltip" v-show="showRightTooltip">
        {{ Math.round((height - bottom) / scale + min) }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VerticalSlider',
  model: {
    prop: 'value',
    event: 'change'
  },
  props: {
    min: {
      type: Number,
      default: 0
    },
    max: {
      type: Number,
      default: 100
    },
    initialValue: {
      type: [Number, Array],
      default: () => [0, 100]
    },
    height: {
      type: Number,
      default: 50
    },
    disabled: {
      type: Boolean,
      default: false
    },
    range: {
      type: Boolean,
      default: false
    },
    value: {
      type: [Number, Array],
      default: null
    },
    showTooltip: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      transition: false,
      timer: null,
      top: 0,
      bottom: 0,
      showLeftTooltip: false,
      showRightTooltip: false
    }
  },
  computed: {
    scale() {
      return this.height / (this.max - this.min)
    },
    sliderValue() {
      const low = Math.round((this.height - this.bottom) / this.scale + this.min)
      if (this.range) {
        const high = Math.round((this.height - this.top) / this.scale + this.min)
        return [high, low]
      }
      return low
    }
  },
  watch: {
    sliderValue(to) {
      this.$emit('change', to)
    }
  },
  created() {
    if (this.range) {
      const highVal = (this.value && (this.value[0] === 0 || this.value[0])) ? this.value[0] : this.initialValue[0]
      this.top = this.height - (highVal - this.min) * this.scale
      const lowVal = (this.value && (this.value[1] === 0 || this.value[1])) ? this.value[1] : this.initialValue[1]
      this.bottom = this.height - (lowVal - this.min) * this.scale
    } else {
      const lowVal = this.value ? this.value : (Array.isArray(this.initialValue) ? 100 : this.initialValue)
      this.bottom = this.height - (lowVal - this.min) * this.scale
    }
  },
  methods: {
    onClickPoint(e) {
      if (this.transition) {
        clearTimeout(this.timer)
        this.timer = null
      } else {
        this.transition = true
      }
      this.timer = setTimeout(() => {
        this.transition = false
      }, 300)

      const containerRect = this.$refs.slider.getBoundingClientRect()
      const clickY = e.clientY - containerRect.top

      if (this.range) {
        const topDistance = Math.abs(clickY - this.top)
        const bottomDistance = Math.abs(clickY - this.bottom)

        if (clickY < this.top) {
          this.top = clickY
          this.showLeftTooltip = true
          setTimeout(() => { this.showLeftTooltip = false }, 1000)
        } else if (clickY > this.bottom) {
          this.bottom = clickY
          this.showRightTooltip = true
          setTimeout(() => { this.showRightTooltip = false }, 1000)
        } else {
          if (topDistance <= bottomDistance) {
            this.top = clickY
            this.showLeftTooltip = true
            setTimeout(() => { this.showLeftTooltip = false }, 1000)
          } else {
            this.bottom = clickY
            this.showRightTooltip = true
            setTimeout(() => { this.showRightTooltip = false }, 1000)
          }
        }
      } else {
        this.bottom = clickY
        this.showRightTooltip = true
        setTimeout(() => { this.showRightTooltip = false }, 1000)
      }

      this.updateSlider()
    },
    onLeftMouseDown() {
      this.showLeftTooltip = true
      const containerTop = this.$refs.slider.getBoundingClientRect().top
      document.onmousemove = (e) => {
        let moveY = e.clientY - containerTop
        if (moveY < 0) {
          this.top = 0
        } else if (moveY >= 0 && moveY <= this.bottom) {
          this.top = moveY
        } else {
          this.top = this.bottom
        }
        this.updateSlider()
      }
      document.onmouseup = () => {
        document.onmousemove = null
        setTimeout(() => { this.showLeftTooltip = false }, 1000)
      }
    },
    onRightMouseDown() {
      this.showRightTooltip = true
      const containerTop = this.$refs.slider.getBoundingClientRect().top
      document.onmousemove = (e) => {
        let moveY = e.clientY - containerTop
        if (moveY > this.height) {
          this.bottom = this.height
        } else if (moveY >= this.top && moveY <= this.height) {
          this.bottom = moveY
        } else {
          this.bottom = this.top
        }
        this.updateSlider()
      }
      document.onmouseup = () => {
        document.onmousemove = null
        setTimeout(() => { this.showRightTooltip = false }, 1000)
      }
    },
    updateSlider() {
      this.$emit('change', this.sliderValue)
    }
  }
}
</script>

<style lang="less" scoped>
@themeColor: #AA81FF;

.m-slider.vertical {
  display: inline-block;
  width: 6px;
  position: relative;
  touch-action: none;

  .u-slider-rail {
    position: absolute;
    z-index: 99;
    width: 6px;
    height: 100%;
    background: #C7C7FF;
    border-radius: 2px;
    cursor: pointer;
    transition: background .3s;
  }

  .u-slider-track {
    position: absolute;
    z-index: 99;
    width: 6px;
    background: #AA81FF;
    border-radius: 6px;
    cursor: pointer;
    pointer-events: none;
    transition: background .3s;
  }

  .trackTransition {
    transition: top .2s, height .2s, background .3s;
  }

  &:hover {
    .u-slider-rail {
      background: #E3E3E3;
    }
    .u-slider-track {
      background: @themeColor;
    }
  }

  .u-slider-handle {
    position: absolute;
    z-index: 999;
    width: 12px;
    height: 12px;
    left: -3px;
    background: #fff;
    border: 2px solid #AA81FF;
    border-radius: 50%;
    cursor: pointer;
    transition: border-color .3s, box-shadow .6s, transform .3s cubic-bezier(.18,.89,.32,1.28);

    &:focus {
      border-color: @themeColor;
      box-shadow: 0 0 0 5px rgba(24, 144, 255, 0.2);
    }

    &:hover {
      border-color: @themeColor;
    }

    .slider-tooltip {
      position: absolute;
      left: 100%;
      top: 50%;
      transform: translateY(-50%);
      margin-left: 8px;
      padding: 4px 8px;
      background: #DAC5EC;
      color: white;
      border-radius: 4px;
      font-size: 12px;
      white-space: nowrap;

      &::after {
        content: "";
        position: absolute;
        left: -4px;
        top: 50%;
        transform: translateY(-50%);
        border-width: 4px;
        border-style: solid;
        border-color: transparent #DAC5EC transparent transparent;
      }
    }
  }

  .handleTransition {
    transition: top .2s, border-color .3s, box-shadow .6s, transform .3s cubic-bezier(.18,.89,.32,1.28);
  }
}

.disabled {
  cursor: not-allowed;

  .u-slider-rail {
    pointer-events: none;
  }

  .u-slider-track {
    background: rgba(0, 0, 0, .25);
  }

  .u-slider-handle {
    border-color: rgba(0, 0, 0, .25);
    pointer-events: none;
  }

  &:hover {
    .u-slider-track {
      background: rgba(0, 0, 0, .25);
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/tcode/remote/VerticalSlider.vue